import { useState, useCallback, useRef, useEffect } from 'react';
import { validationService } from '../services/validationService';
import { isValueComplete } from '../utils/validationUtils';

export interface FieldValidationState {
  loading: boolean;
  valid: boolean | null;
  suggestion: string | null;
  suggestions: string[];
  error: string | null;
  correctedValue?: any;
  requestId: string | null;
}

export type ValidationStates = Record<string, FieldValidationState>;

const defaultFieldState: FieldValidationState = {
  loading: false,
  valid: null,
  suggestion: null,
  suggestions: [],
  error: null,
  correctedValue: null,
  requestId: null,
};

export function useAiValidation() {
  const [validationStates, setStates] = useState<ValidationStates>({});
  const statesRef = useRef<ValidationStates>({});
  const timersRef = useRef<Record<string, any>>({});

  // Keep a ref of states so we can read the latest value inside async callbacks without re-triggering effects
  useEffect(() => {
    statesRef.current = validationStates;
  }, [validationStates]);

  // Clean up all timers when component unmounts
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
    };
  }, []);

  const clearValidation = useCallback((fieldId: string) => {
    if (timersRef.current[fieldId]) {
      clearTimeout(timersRef.current[fieldId]);
      delete timersRef.current[fieldId];
    }
    setStates(prev => {
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  const validate = useCallback(
    (
      fieldId: string,
      type:
        | 'email'
        | 'phone'
        | 'gst'
        | 'pan'
        | 'address'
        | 'dimension'
        | 'shipmentAddress'
        | 'shipmentPackage'
        | string,
      value: any,
      debounceMs = 600,
    ) => {
      // 1. Check if the value is complete/valid for API call
      const isComplete = isValueComplete(type, value, fieldId);

      if (!isComplete) {
        // Clear timers and loading state if the value isn't complete (e.g. empty or partially entered)
        if (timersRef.current[fieldId]) {
          clearTimeout(timersRef.current[fieldId]);
          delete timersRef.current[fieldId];
        }
        setStates(prev => {
          // If the field is not complete, we reset its validation state so we don't display old errors or success checks
          if (!prev[fieldId]) return prev;
          return {
            ...prev,
            [fieldId]: defaultFieldState,
          };
        });
        return;
      }

      // 2. Generate a unique request ID to track this request
      const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // 3. Update state to loading and save the latest requestId
      setStates(prev => ({
        ...prev,
        [fieldId]: {
          ...(prev[fieldId] || defaultFieldState),
          loading: true,
          requestId,
        },
      }));

      // 4. Cancel previous timer for this specific field
      if (timersRef.current[fieldId]) {
        clearTimeout(timersRef.current[fieldId]);
      }

      // 5. Schedule debounced validation API call
      timersRef.current[fieldId] = setTimeout(async () => {
        try {
          const result = await validationService.validate(fieldId, type, value);

          // 6. Check if this is still the latest request for this field (prevent race conditions)
          const currentState = statesRef.current[fieldId];
          if (currentState && currentState.requestId === requestId) {
            if (result.isStale) return; // ignore if validation service marked it as stale

            const hasActualCorrectedValue =
              (result as any).correctedValue !== undefined && (result as any).correctedValue !== null;

            const suggestionsList = Array.isArray(result.suggestions)
              ? result.suggestions
              : result.suggestion
              ? [result.suggestion]
              : [];

            setStates(prev => ({
              ...prev,
              [fieldId]: {
                loading: false,
                valid: result.valid,
                suggestion: hasActualCorrectedValue ? (result as any).correctedValue : null,
                suggestions: suggestionsList,
                error: result.valid
                  ? null
                  : suggestionsList.length > 0
                  ? suggestionsList.join('\n')
                  : result.error || `Invalid ${type.toUpperCase()}`,
                correctedValue: hasActualCorrectedValue ? (result as any).correctedValue : null,
                requestId,
              },
            }));
          }
        } catch (error) {
          console.error(`AI validation failed for field ${fieldId}:`, error);

          // 7. Graceful error handling: stop loading but preserve existing state
          const currentState = statesRef.current[fieldId];
          if (currentState && currentState.requestId === requestId) {
            setStates(prev => ({
              ...prev,
              [fieldId]: {
                ...(prev[fieldId] || defaultFieldState),
                loading: false,
              },
            }));
          }
        } finally {
          delete timersRef.current[fieldId];
        }
      }, debounceMs);
    },
    [],
  );

  return {
    validationStates,
    validate,
    clearValidation,
    setStates,
  };
}

export default useAiValidation;
